import { MarketButton, MarketProgressTracker, MarketProgressTrackerStep } from '@market/react';
import { useTranslation } from 'react-i18next';
import { withI18n } from '@squareup/service-i18n/components';
import { useResponsiveBreakpoint } from '@squareup/ui-responsive-window';
import styles from './Tracker.module.css';

export enum TrackerStatus {
  Processing = 'bankingShared.card.tracker.step.processing',
  Shipped = 'bankingShared.card.tracker.step.shipped',
  Delivered = 'bankingShared.card.tracker.step.delivered',
}

const TRACKER_STEP_KEYS = Object.values(TrackerStatus).map((key) => (key));

const getStepIndex = (status: TrackerStatus): number => Object.values(TrackerStatus).indexOf(status) + 1;

export interface TrackerProps {
  readonly cardName: string;
  readonly arrivalDate: Date;
  readonly status: TrackerStatus;
  readonly onActivateCardClick: () => void;
}

export const Tracker = ({
  cardName, arrivalDate, status, onActivateCardClick,
}: TrackerProps) => {
  const { t, i18n } = useTranslation();
  const localeDate = arrivalDate.toLocaleDateString(i18n.language, {
    month: 'short',
    day: 'numeric',
  });
  const { isMobile } = useResponsiveBreakpoint();

  const descriptionTextKey = status === TrackerStatus.Delivered ? 'bankingShared.card.tracker.description.delivered' : 'bankingShared.card.tracker.description.notDelivered';
  const canDisplayActivate = status === TrackerStatus.Delivered || status === TrackerStatus.Shipped;

  return (<div className={styles.card}>
    <div className={styles.cardWrapper}>
      <div className={styles.info}>
        <p className="paragraph-20" data-testid="card-tracker-description">{t(descriptionTextKey, {
          cardName,
          arrivalDate: localeDate,
        })}</p>
        <MarketProgressTracker
          className={styles.tracker}
          currentStepIndex={getStepIndex(status)}
          orientation={isMobile ? 'vertical' : 'horizontal'}
          indicator="check"
          size="small"
        >
          {TRACKER_STEP_KEYS.map((step) => (<MarketProgressTrackerStep key={step}>
            <label slot="label">{t(step)}</label>
          </MarketProgressTrackerStep>))}
        </MarketProgressTracker>
      </div>
      {canDisplayActivate && <MarketButton
        rank={status === TrackerStatus.Delivered ? 'primary' : 'secondary'}
        size="small"
        role="button"
        className={styles.button}
        onClick={onActivateCardClick}
      >
        {t('bankingShared.card.tracker.button')}
      </MarketButton>}
    </div>
  </div>);
};

export const TrackerI18n = withI18n(Tracker);
